
import React from 'react';
import { Rule, RulesEnableStatus } from '../types.ts';
import { Edit2, Trash2, Power, ArrowRight, Layers, GitBranch, Hash, Tag, AlertTriangle, Sparkles, Lock } from 'lucide-react';
import { PROVIDER_ICONS } from '../constants.ts';
import { SlackChannel } from '@/services/apiService';

interface RuleListProps {
  rules: Rule[];
  onEdit: (rule: Rule) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
  slackChannels?: SlackChannel[];
  rulesStatus?: RulesEnableStatus | null;
  onUpgrade?: () => void;
  loading?: boolean;
}

const RuleCardSkeleton: React.FC = () => (
  <div className="card bg-white rounded-2xl border border-[#e4e0d2] p-4 md:p-6 animate-pulse">
    {/* Header Section */}
    <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 md:mb-6 gap-4">
      <div className="flex items-start gap-3 md:gap-5 min-w-0 w-full">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#e4e0d2] border border-[#e4e0d2] shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-5 bg-[#e4e0d2] rounded-md w-1/3" />
          <div className="h-4 bg-[#efebdf] rounded-md w-2/3" />
        </div>
      </div>
      <div className="flex items-center gap-2 md:self-start shrink-0">
        <div className="w-10 h-10 rounded-xl bg-[#e4e0d2]" />
        <div className="w-10 h-10 rounded-xl bg-[#efebdf]" />
        <div className="w-10 h-10 rounded-xl bg-[#efebdf]" />
      </div>
    </div>
    {/* Logic Visualizer */}
    <div className="bg-[#f4f1e6] p-3 md:p-4 rounded-xl border border-[#e4e0d2]">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-8 bg-[#e4e0d2] rounded-lg w-40" />
        <div className="h-5 w-5 bg-[#e4e0d2] rounded" />
        <div className="h-8 bg-[#e4e0d2] rounded-lg w-32" />
      </div>
    </div>
  </div>
);

export const RuleList: React.FC<RuleListProps> = ({
  rules,
  onEdit,
  onDelete,
  onToggle,
  slackChannels = [],
  rulesStatus,
  onUpgrade,
  loading = false
}) => {
  if (loading) {
    return (
      <div className="grid gap-4 md:gap-5" role="status" aria-busy="true" aria-label="Loading rules">
        <span className="sr-only">Loading rules…</span>
        {Array.from({ length: 3 }).map((_, i) => (
          <RuleCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Check if a rule can be enabled based on plan limits
  const canEnableRule = (rule: Rule): boolean => {
    if (rule.isActive) return true; // Can always disable
    if (!rulesStatus) return true; // If no status, allow
    return rulesStatus.can_enable_more;
  };

  const isFreeTierLimited = rulesStatus?.plan === 'free' && !rulesStatus.can_enable_more;

  if (rules.length === 0) {
    return (
      <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-[#d3cdba]">
        <div className="bg-[#f4f1e6] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-[#9a948a]">
          <Layers size={40} />
        </div>
        <h3 className="text-lg font-bold text-[#0a0a0a] mb-2">No rules yet</h3>
        <p className="text-[#6f6b60] max-w-sm mx-auto px-4">Create your first automation rule to start routing your repository events.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Free tier limit banner */}
      {isFreeTierLimited && (
        <div className="bg-[#f3e8ff] border border-[#e4e0d2] rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#f3e8ff] p-2 rounded-lg">
              <Sparkles size={18} className="text-[#7e22ce]" />
            </div>
            <div>
              <p className="font-bold text-[#0a0a0a] text-sm">Free tier: 1 active rule limit</p>
              <p className="text-[#4f4f48] text-xs">Upgrade to Pro for unlimited active rules</p>
            </div>
          </div>
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              className="bg-[#22c55e] text-[#0a0a0a] font-bold px-4 py-2 rounded-xl text-sm hover:bg-[#16a34a] transition-colors"
            >
              Upgrade
            </button>
          )}
        </div>
      )}

      <div className="grid gap-4 md:gap-5">
        {rules.map((rule) => (
        <div 
          key={rule.id} 
          className={`card card-hover bg-white rounded-2xl border border-[#e4e0d2] p-4 md:p-6 ${
            rule.isActive
              ? 'border-l-2 border-l-[#22c55e]'
              : 'opacity-75'
          }`}
        >
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 md:mb-6 gap-4">
            <div className="flex items-start gap-3 md:gap-5 min-w-0 w-full">
              <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shrink-0 ${rule.isActive ? 'bg-[#22c55e] text-white' : 'bg-[#ece8db] text-[#9a948a]'}`}>
                <GitBranch size={24} strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-[#0a0a0a] text-lg md:text-xl flex flex-wrap items-center gap-2 mb-1">
                  <span className="break-words leading-tight">{rule.name}</span>
                  {!rule.isActive && <span className="text-xs bg-[#ece8db] text-[#6f6b60] px-2 py-1 rounded-md font-bold uppercase shrink-0">Inactive</span>}
                </h3>
                <p className="text-[#6f6b60] font-medium text-sm md:text-base leading-snug break-words">{rule.description}</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center justify-center md:justify-end gap-2 md:self-start mt-2 md:mt-0 pt-3 md:pt-0 border-t border-[#e4e0d2] md:border-t-0 w-full md:w-auto shrink-0">
              {/* Toggle Button with Plan Limit Handling */}
              <div className="relative group">
                <button
                  onClick={() => canEnableRule(rule) && onToggle(rule.id)}
                  disabled={!canEnableRule(rule)}
                  title={
                    !canEnableRule(rule)
                      ? "Free tier: 1 active rule limit. Upgrade to Pro for unlimited rules."
                      : rule.isActive
                        ? "Disable Rule"
                        : "Enable Rule"
                  }
                  className={`p-2.5 rounded-xl transition-colors font-bold ${
                    !canEnableRule(rule)
                      ? 'text-[#9a948a] cursor-not-allowed'
                      : rule.isActive
                        ? 'text-[#22c55e] hover:bg-[#dcfce7]'
                        : 'text-[#6f6b60] hover:bg-[#dcfce7] hover:text-[#22c55e]'
                  }`}
                >
                  {!canEnableRule(rule) ? (
                    <Lock size={20} strokeWidth={2.5} />
                  ) : (
                    <Power size={20} strokeWidth={2.5} />
                  )}
                </button>

                {/* Upgrade tooltip on hover for disabled toggle */}
                {!canEnableRule(rule) && onUpgrade && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-[#0a0a0a] text-white text-xs font-medium px-3 py-2 rounded-lg shadow-soft whitespace-nowrap">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Sparkles size={12} className="text-[#fcd34d]" />
                        Free tier: 1 active rule
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpgrade();
                        }}
                        className="text-[#86efac] hover:text-[#bbf7d0] underline"
                      >
                        Upgrade to Pro
                      </button>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0a0a0a]" />
                    </div>
                  </div>
                )}
              </div>
              <button 
                onClick={() => onEdit(rule)}
                className="p-2.5 text-[#6f6b60] hover:text-[#075985] hover:bg-[#e0f2fe] rounded-xl transition-colors"
                title="Edit Rule"
              >
                <Edit2 size={20} strokeWidth={2.5} />
              </button>
              <button 
                onClick={() => onDelete(rule.id)}
                className="p-2.5 text-[#6f6b60] hover:text-[#be123c] hover:bg-[#ffe4e6] rounded-xl transition-colors"
                title="Delete Rule"
              >
                <Trash2 size={20} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Logic Visualizer - Fluid Flow Layout */}
          <div className="bg-[#f4f1e6] p-3 md:p-4 rounded-xl border border-[#e4e0d2]">
             <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
               
               {/* Source Block */}
               <div className="flex items-center gap-2 bg-white px-2 py-1.5 border border-[#e4e0d2] rounded-lg max-w-full min-w-0">
                  <span className="font-bold uppercase text-[10px] tracking-wider bg-[#ece8db] text-[#6f6b60] px-1.5 py-0.5 rounded shrink-0">Source</span>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {React.createElement(PROVIDER_ICONS[rule.source.provider], { size: 14, className: 'text-[#6f6b60] shrink-0' })}
                    <span className="font-bold text-[#0a0a0a] text-sm md:text-base break-all truncate">
                      {rule.source.repository}
                    </span>
                  </div>
               </div>
               
               {/* Filters (Optional) */}
               {rule.source.filters && (
                 <>
                   {rule.source.filters.teams.map(team => (
                     <div key={team} className="flex items-center gap-1 bg-[#f3e8ff] text-[#7e22ce] px-2 py-1 rounded-lg border border-[#e9d5ff] text-xs font-bold shrink-0">
                       <Hash size={10} /> {team}
                     </div>
                   ))}
                   {rule.source.filters.labels.map(label => (
                     <div key={label} className="flex items-center gap-1 bg-[#fef3c7] text-[#92600a] px-2 py-1 rounded-lg border border-[#fde68a] text-xs font-bold shrink-0">
                       <Tag size={10} /> {label}
                     </div>
                   ))}
                 </>
               )}

               {/* Flow Indicator */}
               <ArrowRight size={20} className="text-[#c9c3b0] shrink-0" strokeWidth={3} />
               
               {/* Target Block */}
               <div className="flex flex-wrap items-center gap-2 max-w-full">
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-bold uppercase text-[10px] tracking-wider bg-[#ece8db] text-[#6f6b60] px-1.5 py-0.5 rounded">To</span>
                  </div>
                  
                  {rule.targets.map((target) => {
                    const slackChannel = target.provider === 'slack'
                      ? slackChannels.find(ch => ch.id === target.target)
                      : null;
                    const hasWarning = slackChannel && !slackChannel.is_member;

                    return (
                      <div
                        key={target.id}
                        className={`flex items-center gap-2 bg-white px-3 py-1.5 border rounded-lg max-w-full min-w-0 ${hasWarning ? 'border-[#fcd34d]' : 'border-[#e4e0d2]'}`}
                        title={hasWarning ? `ReviewMe is not in #${slackChannel.name}. Notifications will fail.` : undefined}
                      >
                        {hasWarning && (
                          <AlertTriangle size={14} className="text-[#d97706] shrink-0" />
                        )}
                        {React.createElement(PROVIDER_ICONS[target.provider], { size: 14, className: 'text-[#0a0a0a] shrink-0' })}
                        <span className="text-sm font-semibold text-[#0a0a0a] truncate min-w-0">{target.targetName || target.target}</span>
                      </div>
                    );
                  })}
               </div>

             </div>
          </div>
        </div>
        ))}
      </div>
    </div>
  );
};
